import React from 'react';
import { CalendarMonthOutlined, EventOutlined, InsertDriveFileOutlined, QuestionMark } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { ActivityFeedGroup } from '../../../../components/ActivityFeed';
import { BaseActivityItem } from '../../../../components';
import { StateLabel } from '../../../../components/StatesLabel';
import { useCollaborativeProviderActivityQuery } from '@uplift/queries';
import { ChartsReviewCompletedCollaborativeProviderActivity, CollaborativeProviderActivity, CollaborativeProviderActivityType, SupervisionMeetingCollaborativeProviderActivity } from '@uplift/api';
import { DateFormats, providerChartsReviewRoute } from '@uplift/constants';
import { useHistory } from 'react-router';
import { Loader, useOnScreenObserver } from '@uplift/ui-v2';


const AgreementSignedActivityItem = (props: {
  providerId: string;
  stateId: string;
  onClick: () => void;
}) => {
  return (
    <BaseActivityItem
      icon={InsertDriveFileOutlined}
      title="Collaboration Agreement signed"
      rightContent={<Typography variant="body2"><StateLabel stateId={props.stateId} /></Typography>}
    />
  );
};


const SupervisionMeetingActivityItem = (props: {
  providerId: string;
  activity: SupervisionMeetingCollaborativeProviderActivity;
  onClick: () => void;
}) => {
  return (
    <BaseActivityItem
      icon={CalendarMonthOutlined}
      title="Supervision Meeting"
      rightContent={<Typography variant="body2"><StateLabel stateId={props.activity.stateId} /></Typography>}
      color="secondary"
      onClick={props.onClick}
    >
      <Typography color="text.secondary">
        <Box component="span" sx={{ fontWeight: 500, color: 'text.primary' }}>Duration:</Box>{' '}
        {props.activity.durationMinutes} minutes · {props.activity.summary}
      </Typography>
    </BaseActivityItem>
  );
};


const ChartsReviewCompletedActivityItem = (props: {
  providerId: string;
  activity: ChartsReviewCompletedCollaborativeProviderActivity;
}) => {
  const history = useHistory();

  return (
    <BaseActivityItem
      icon={EventOutlined}
      title="Charts Review"
      rightContent={<Typography variant="body2"><StateLabel stateId={props.activity.stateId} /></Typography>}
      color="secondary"
      onClick={() => history.push(providerChartsReviewRoute.createLink({ reviewId: props.activity.reviewId }))}
    >
      <Typography>
        Submitted at {CalendarDateService.date(props.activity.datetime).format(DateFormats['MMM D, YYYY'])}
      </Typography>
    </BaseActivityItem>
  );
};


const ActivityItem: React.VFC<{
  activity: CollaborativeProviderActivity;
  providerId: string;
  onClick: () => void;
}> = ({ activity, providerId, onClick }) => {
  if (activity.activityType === CollaborativeProviderActivityType.agreementSigned) {
    return (
      <AgreementSignedActivityItem
        providerId={providerId}
        stateId={activity.stateId}
        onClick={onClick}
      />
    );
  }

  if (activity.activityType === CollaborativeProviderActivityType.supervisionMeeting) {
    return (
      <SupervisionMeetingActivityItem
        providerId={providerId}
        activity={activity}
        onClick={onClick}
      />
    );
  }

  if (activity.activityType === CollaborativeProviderActivityType.chartsReviewCompleted) {
    return (
      <ChartsReviewCompletedActivityItem
        providerId={providerId}
        activity={activity}
      />
    );
  }
  return (
    <BaseActivityItem
      icon={QuestionMark}
      title={`unknown activity | ${activity.activityType}`}
      color="warning"
    />
  );
};


export type ActivityFeedProps = {
  providerId: string;
  stateId: string | null;
  onSelectActivity: (activityId: string) => void;
};

export const ActivityFeed = ({ providerId, onSelectActivity, stateId }: ActivityFeedProps) => {
  const l10n = useL10n();
  const timezone = useTimezone();
  const now = React.useMemo(() => CalendarDateService.date().tz(timezone), [ timezone ]);
  const { data, isInitialLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useCollaborativeProviderActivityQuery(providerId, { stateId: stateId });
  const loaderRef = useOnScreenObserver(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  });

  if (isInitialLoading) {
    return (
      <Loader color="secondary" size="2rem" mt={3} />
    );
  }

  if (!data?.pages?.[0]?.data.length) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary" textAlign="center">
          {l10n.supervisees.noActivities}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {data?.pages?.map((resp, index, arr) => (
        <React.Suspense key={resp.cursor || 'END'} fallback={null}>
          <ActivityFeedGroup
            now={now}
            items={resp.data}
            prevItems={arr?.[index - 1]?.data}
            getDate={activity => activity.datetime}
            getKey={activity => activity.id}
            renderItem={activity => (
              <ActivityItem
                providerId={providerId}
                activity={activity}
                onClick={() => onSelectActivity(activity.id)}
              />
            )}
          />
        </React.Suspense>
      ))}
      {hasNextPage && (
        <div
          ref={loaderRef}
          key={isFetchingNextPage ? 'loading' : 'load-more'}
        >
          <Loader color="secondary" size="2rem" mt={3} />
        </div>
      )}
    </Box>
  );
};
